import type { BatchDeleteScope, IBatchDeletePayload, TransactionBackendStatus } from "@types";
import type { BatchSelectionItem } from "@screens/shared/useBatchSelection";

export type BatchDeleteModule = "DESPESA" | "RECEITA";

export interface RecurrenceSeries {
  recurrenceId: string;
  items: BatchSelectionItem[];
}

export const DEFAULT_RECURRENCE_SCOPE: BatchDeleteScope = "SINGLE";

export const STATUS_ORDER: TransactionBackendStatus[] = ["REALIZED", "FORECASTED", "OVERDUE"];

export const groupRecurrenceSeries = (items: BatchSelectionItem[]): RecurrenceSeries[] => {
  const series = new Map<string, BatchSelectionItem[]>();

  items.forEach((item) => {
    if (!item.recurrenceId) return;
    const current = series.get(item.recurrenceId) ?? [];
    series.set(item.recurrenceId, [...current, item]);
  });

  return Array.from(series.entries()).map(([recurrenceId, seriesItems]) => ({ recurrenceId, items: seriesItems }));
};

export const countByStatus = (items: BatchSelectionItem[]): Record<TransactionBackendStatus, number> =>
  items.reduce<Record<TransactionBackendStatus, number>>(
    (acc, item) => {
      if (acc[item.status] !== undefined) acc[item.status] += 1;
      return acc;
    },
    { REALIZED: 0, FORECASTED: 0, OVERDUE: 0 }
  );

export const buildBatchDeletePayload = (
  items: BatchSelectionItem[],
  scopes: Record<string, BatchDeleteScope>
): IBatchDeletePayload => ({
  items: items.map(({ id, version }) => ({ id, version })),
  recurrenceScopes: groupRecurrenceSeries(items).map(({ recurrenceId }) => ({
    recurrenceId,
    scope: scopes[recurrenceId] ?? DEFAULT_RECURRENCE_SCOPE,
  })),
});

export const resolveModuleKey = (module: BatchDeleteModule) => (module === "RECEITA" ? "receipt" : "expense");

const LOCAL_STATUS_KEY: Record<BatchDeleteModule, Record<TransactionBackendStatus, string>> = {
  DESPESA: { REALIZED: "realizado", FORECASTED: "previsto", OVERDUE: "vencido" },
  RECEITA: { REALIZED: "realizado", FORECASTED: "previsto", OVERDUE: "emAtraso" },
};

export const resolveStatusLabelKey = (module: BatchDeleteModule, status: TransactionBackendStatus) =>
  `${resolveModuleKey(module)}.status.${LOCAL_STATUS_KEY[module][status]}`;

export const formatCurrency = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
